import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLoaderData } from 'react-router-dom';

import Descriptor from '@r6digital/rapidstor-types/react/entities/Descriptor';
import Location from '@r6digital/rapidstor-types/react/entities/Location';
import UnitType from '@r6digital/rapidstor-types/react/entities/UnitType';

import {
    unitTypeDealInclusions,
    unitTypeDealSavingsPercentage,
    unitTypeFormattedRate,
    unitTypeHasMonetaryDiscount,
    unitTypeValueRate,
} from '@/core/utils/rapidstor';

import Button from '../../../core/components/elements/button';

import { ANIMATION_SPEED } from '@/features/rapidstor/animations';
import {
    descriptorTitle,
    formatDealCustomText,
    formatTimeframe,
    isDescriptorAvailable,
    unitTypeHasDeal,
} from '@/features/rapidstor/helpers';

import classes from './PopCard.module.scss';

import Inclusions from '../descriptor/inclusion';
import DescriptorTag from '../descriptor/tag';

interface Props {
    isDeal: boolean;
    index: number;
    card: UnitType;
    descriptor: Descriptor;
    isOpen: boolean;
    onContinueEvent: (card: UnitType) => void;
    setCarouselHidden?: (hidden: boolean) => void;
    doOpenCard: (index: number) => void;
}

export default function PopCard({
    isDeal,
    index,
    card,
    descriptor,
    isOpen,
    onContinueEvent,
    setCarouselHidden,
    doOpenCard,
}: Props) {
    const location = useLoaderData() as Location;
    const { t } = useTranslation();

    const descriptions = !isDeal ? descriptor.descriptions : unitTypeDealInclusions(card);
    const isMonetaryDeal = isDeal && unitTypeHasMonetaryDiscount(card);
    const hasCustomFormat = !!card.deal?.customFormat.primary && card.deal.customFormat.primary !== '';

    return (
        <>
            <motion.div
                initial={{
                    opacity: !isOpen ? 0 : 0.7,
                    // height: !isOpen ? 0 : 'auto',
                }}
                animate={{
                    opacity: !isOpen ? 0.7 : 0,
                    // height: !isOpen ? 'auto' : 0,
                }}
                transition={{ duration: ANIMATION_SPEED }}
                className={classes.click_to_view}
            >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <g>
                        {/* eslint-disable-next-line max-len */}
                        <path d="M11.2815 16.254L10.263 12.45M10.263 12.45L8.3805 14.1188L8.80725 7.01625L12.7275 12.954L10.263 12.45ZM9 1.6875V3.375M13.3755 3.4995L12.1823 4.69275M15.1875 7.875H13.5M5.81775 11.0573L4.62525 12.2498M4.5 7.875H2.8125M5.81775 4.69275L4.62525 3.50025" stroke="white" strokeWidth="1.125" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                </svg>
                <span>{t('book_unit:click_to_view')}</span>
            </motion.div>
            <div className={classes.header}>
                {/* TITLE */}
                <div className={classes.first}>
                    <span>
                        <label className={`${classes.underline}`}>
                            {descriptorTitle(descriptor, location)[1]}
                        </label>
                        {' '}
                        {unitTypeHasDeal(card) && isDeal ? (<div className={`${classes.deal_title}`}>{card.deal?.title}</div>) : ''}
                    </span>
                </div>
                {/*  */}
                <div className={classes.last}>
                    <span>{unitTypeFormattedRate(card, location)}</span>
                    <span>
                        {isMonetaryDeal && hasCustomFormat ? (
                            <div
                                className={`${classes.deal_copy} ${classes.custom_format}`}
                            >
                                {card.deal?.customFormat.primary && (
                                    <span>
                                        {formatDealCustomText(
                                            card.deal,
                                            location,
                                            card.deal.customFormat.primary,
                                        )}
                                    </span>
                                )}
                                {' '}
                                {card.deal?.customFormat.secondary && (
                                    <span>
                                        {formatDealCustomText(
                                            card.deal,
                                            location,
                                            card.deal.customFormat.secondary,
                                        )}
                                    </span>
                                )}
                            </div>
                        ) : null}
                        {isMonetaryDeal && !hasCustomFormat ? (
                            <>
                                {`${t('unit_card:valued_at')} ${unitTypeValueRate(
                                    card,
                                    location,
                                )}`}
                                <span className={classes.savings_badge}>
                                    {unitTypeDealSavingsPercentage(card)}
                                </span>
                            </>
                        ) : null}
                        {!isMonetaryDeal ? formatTimeframe(location) : null}
                    </span>
                </div>
            </div>

            {/* INCLUSIONS */}
            {descriptions && <Inclusions inclusions={descriptions} />}

            {/* DESCRIPTOR TAGS */}
            {isDeal && card?.deal && card.deal.tags ? (
                <DescriptorTag secondary tags={card?.deal?.tags} />
            ) : (
                <DescriptorTag secondary tags={descriptor.tags} />
            )}

            {/* CONTINUE BUTTONS */}
            <Button
                onclick={() => {
                    if (isDescriptorAvailable(descriptor)) {
                        doOpenCard(index);
                        if (setCarouselHidden) {
                            setCarouselHidden(true);
                        }
                    } else if (location.rapidstor.allowSaveQuote) {
                        onContinueEvent(card);
                    } else {
                        window.location.href = `mailto:${location.corpCode === 'CRUK' ? t('contact_card:emailOverride') : location.contactEmail}`;
                    }
                }}
                type="textOnPrimary"
                size="medium"
                id="continue_button"
            >
                {isDescriptorAvailable(descriptor)
                    ? t('cta:step_3:continue')
                    : t('cta:enquire_now')}
            </Button>
            {!isDescriptorAvailable(descriptor) && (
                <a
                    className={`${classes.callNowLink}`}
                    href={`tel:${location.contactPhone}`}
                >
                    <span dangerouslySetInnerHTML={{ __html: t('cta:call_us_now') }} />
                </a>
            )}
        </>
    );
}
